import React, { FC, useEffect, useRef, useState } from "react";
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    Image,
} from "@smile2impress/components/relaunch";
import { LogoIcon } from "@smile2impress/components";
import { useErrorContext } from "context/errorContext";
import { ErrorType } from "layouts/relaunch/pages/ErrorLayout";

import DoneIcon from "components/Icons/icons/24/done.svg";
import { getRefCodeFromQuery } from "utils/referral";
import { UrlsConfig } from "configs/urlsConfig";

import {
    ReferralBannerProps,
    ReferralBannerGiftCard,
} from "./ReferralBanner.types";

const CARD_TRANSFORMS = ["rotate(2deg)", "rotate(-4deg)"];

const GiftCardItem: FC<{ card: ReferralBannerGiftCard; index: number }> = ({
    card,
    index,
}) => (
    <Flex
        w={["330px", "330px", "541px"]}
        bg={card.bgColor || "base.bitterSweet"}
        borderRadius="sm"
        transform={CARD_TRANSFORMS[index] || "none"}
        p={[2, 2, 3]}
        mb={[2.5, 3, 3]}
        alignItems="center"
    >
        <Flex
            w={["100px", "100px", "160px"]}
            h={["100px", "100px", "160px"]}
            aspectRatio="1/1"
            bg="base.white"
            borderRadius="sm"
            alignItems="center"
            justifyContent="center"
        >
            {card.imageUrl ? (
                <Image src={card.imageUrl} alt={card.title} w="80%" h="auto" />
            ) : (
                <Icon h="auto" w="80%" as={LogoIcon} color="base.logo" />
            )}
        </Flex>
        <Box ml={[1, 1, 2]}>
            <Text textStyle="special3" color="base.white" mb={1}>
                {card.title}
            </Text>
            {card.text && (
                <Text
                    textStyle="special"
                    fontSize={[36, 36, 56]}
                    color={card.textColor || "base.white"}
                >
                    {card.text}
                </Text>
            )}
        </Box>
    </Flex>
);

export const ReferralBanner: FC<ReferralBannerProps> = ({
    content: { title, subtitle, description, buttonText, toastText, cards },
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { setErrorType } = useErrorContext();
    const [showToast, setShowToast] = useState(false);
    const [shortLink, setShortLink] = useState("");

    const copyText = () => {
        if (inputRef.current) {
            inputRef.current.select();
            document.execCommand("copy");
            setShowToast(true);
            setTimeout(() => setShowToast(false), 2000);
        }
    };

    useEffect(() => {
        let link = window?.location?.href;
        const code = getRefCodeFromQuery();

        if (code) {
            link = link.replace(
                UrlsConfig.REFERRAL_FOR_FRIENDS,
                UrlsConfig.REFERRAL
            );
        } else {
            setErrorType(ErrorType.ADVOCATE);
        }

        setShortLink(link);
    }, []);

    return (
        <Flex
            direction={["column", "column", "row"]}
            position="relative"
            width="100%"
            alignItems={["flex-start", "flex-start", "center"]}
            m={[0, 0, 4]}
            gap={[0, 0, 6]}
            justifyContent="center"
            w="100%"
        >
            {cards && cards.length > 0 && (
                <Flex
                    flexDirection="column"
                    alignItems={["center", "center", "flex-start"]}
                    width={["100%", "100%", "50%"]}
                    ml={[0, 0, 1]}
                    mt={[3, 3, 0]}
                    order={[0, 0, 1]}
                >
                    {cards.map((card, index) => (
                        <Box
                            key={index}
                            position="relative"
                            zIndex={index + 1}
                            {...(index > 0 && {
                                mt: ["-30px", "-100px"],
                                ml: [0, 0, "-20px"],
                            })}
                        >
                            <GiftCardItem card={card} index={index} />
                        </Box>
                    ))}
                </Flex>
            )}
            <Box
                bg="base.grey"
                p={[2, 2, 3]}
                borderRadius="md"
                width={["100%", "100%", "50%"]}
                my={2}
            >
                <div>
                    <Heading
                        textStyle="H2"
                        mb={2}
                        textAlign="left"
                        sx={{
                            i: {
                                color: "base.bitterSweet",
                                fontStyle: "normal",
                            },
                        }}
                    >
                        {title}
                    </Heading>
                    {subtitle && <Text textStyle="body">{subtitle}</Text>}
                    <Box className="link" my={1} position="relative">
                        <Box
                            as="input"
                            value={shortLink}
                            ref={inputRef}
                            readOnly
                            w="100%"
                            borderRadius="lg"
                            p={1}
                        />
                        <Button
                            onClick={copyText}
                            position="absolute"
                            right={0.5}
                            height="38px"
                            top="6px"
                            borderRadius="lg"
                            textTransform="uppercase"
                        >
                            <Flex p={2}>{buttonText}</Flex>
                        </Button>
                        {showToast && (
                            <Box
                                position="fixed"
                                bottom={4}
                                left="50%"
                                transform="translateX(-50%)"
                                bg="white"
                                p={3}
                                borderRadius="md"
                                boxShadow="lg"
                            >
                                <Flex alignItems="center">
                                    <Icon>
                                        <DoneIcon />
                                    </Icon>
                                    <Text ml={2}>{toastText}</Text>
                                </Flex>
                            </Box>
                        )}
                    </Box>
                    {description && (
                        <Text
                            sx={{
                                strong: {
                                    textStyle: "title2",
                                    display: "block",
                                },
                            }}
                            mb={0}
                            textAlign="left"
                        >
                            {description}
                        </Text>
                    )}
                </div>
            </Box>
        </Flex>
    );
};
